// run-command-prompt-output-contract-section.ts
//
// The "## Output contract (BẮT BUỘC)" section of the worker prompt — result
// rules plus the minimal fill-in example — split out of
// run-command-prompt-builder.ts to keep that file under the 200-line budget
// (plan 20260924-0205). Pure: same lines, same order as before the split,
// including the leading blank line that separates it from the previous section.
import { resolve } from "node:path";
import { autopilotBindingFilePath } from "./autopilot-binding-file-writer.js";
import { resolveCommandAdapter } from "./command-adapter.js";
import { renderDecisionOptionsContractSection } from "./run-command-prompt-decision-options-contract-section.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { canonicalWorktreePath, WORKTREE_PLACEHOLDER } from "./worktree-bound-tool-rules.js";

/**
 * The `review_report` shape the trailing example shows to the two reviewer
 * adapters. Placeholders on purpose: a real reviewer copied `risk: "low"`
 * verbatim is exactly the "đồng ý theo quán tính" review-clean.md forbids.
 */
const REVIEW_REPORT_EXAMPLE = {
  risk: "<low|medium|high|critical — chấm theo diff, KHÔNG chép mẫu>",
  secret_scan: { verdict: "<clean | findings_present>", findings: 0 },
  evidence_hashes: [],
};

export function renderOutputContractSection(
  ticket: Ticket,
  adapterId: string,
  evidenceRequired: boolean,
  reviewReportRequired: boolean,
  missionWorkspace?: string,
): string[] {
  // V2-1 E3: mục id lựa chọn (tiêu đề cố định riêng) đứng ngay trước contract v1.
  const lines: string[] = [...renderDecisionOptionsContractSection()];

  lines.push("");
  lines.push("## Output contract (BẮT BUỘC)");
  // H12 (2026-09-29): this line used to promise a result-file fallback no
  // controller code ever read — a worker that obeyed it burned three
  // attempts on `missing_result_event`. The terminal result is the only
  // source (terminal-result-extractor.ts: structured_output, else the last
  // balanced JSON object in the result text, fenced or not).
  lines.push(
    "- Trả kết quả theo result.schema.json (đã truyền qua --json-schema). Nguồn kết quả DUY NHẤT là terminal result của phiên (structured_output, không có thì object JSON cuối trong câu trả lời — viết nó thành khối ```json cuối); controller KHÔNG đọc file result nào — ghi result ra file thay cho câu trả lời ⇒ attempt bị từ chối.",
  );
  // Dogfood Beta 12 (2026-10-05): workers spent 2–3 turns per attempt on
  // `cat`/`find` calls naming this file — the pack's dangerous-command guard
  // denies every Bash command that mentions it, by design. Read is not
  // inspected by that guard, so name the tool that works.
  // P-1 (dogfood 2026-10-06): the placeholder read as the OUTER workspace —
  // two wasted Read calls. Name the exact absolute path whenever the caller
  // knows the mission workspace; the placeholder stays only as a fallback.
  // resolve() (review 261006 M2): `run --repo .` yields a relative workspace,
  // resolved against the supervisor's cwd — the worker runs elsewhere.
  const bindingPath =
    missionWorkspace !== undefined
      ? resolve(autopilotBindingFilePath(missionWorkspace))
      : "<thư mục làm việc của phiên>/.claude/state/autopilot-binding.json";
  lines.push(
    `- run_id/session_id: lấy NGUYÊN VĂN từ binding của phiên này — đọc bằng tool Read tại ${bindingPath} (KHÔNG dùng Bash: cat/find/grep nhắc tên file này đều bị guard từ chối) — KHÔNG tự sinh, KHÔNG suy từ prompt.`,
  );
  // Dogfood Beta 12 (mission A2): a real worker chained
  // `npm test; node run-tests-and-report.mjs` into one call; the chain did
  // not match `Bash(npm test*)`, was denied, and the worker reported itself
  // blocked on attempt 1. Spell out the grant shape and that one denial is
  // not a reason to stop.
  // 06g R1b (E34, debate N2): the session binds `{{WORKTREE}}` to the real
  // path (session-runner.ts), so the prompt shows the SAME literal rule and
  // example — a worker told `git -C .` would be denied by its own grant.
  const worktree =
    missionWorkspace !== undefined ? canonicalWorktreePath(missionWorkspace) : "<worktree>";
  const bashGrants = resolveCommandAdapter(adapterId)
    .allowedTools.filter((tool) => tool.startsWith("Bash("))
    .map((tool) => tool.split(WORKTREE_PLACEHOLDER).join(worktree));
  if (bashGrants.length > 0) {
    // Examples only from grants this adapter really has — a reviewer with
    // `git -C * diff*` alone must not be told to try `npm test`.
    const examples = [
      ...(bashGrants.includes("Bash(npm test*)") ? ["`npm test`", "`npm test -- <file>`"] : []),
      ...(bashGrants.includes("Bash(pytest*)") ? ["`pytest <path>`"] : []),
    ];
    const runsTests = examples.length > 0;
    lines.push(
      [
        `- Lệnh shell: chỉ được cấp ${bashGrants.join(", ")}. MỖI lần gọi Bash = ĐÚNG MỘT lệnh khớp một quyền đó`,
        runsTests ? ` (vd ${examples.join(", ")})` : "",
        " — KHÔNG nối bằng ; && || |, KHÔNG `cd … &&`",
        runsTests
          ? ", KHÔNG gọi script của recipe (vd `node run-tests-and-report.mjs`; controller tự chạy recipe)"
          : "",
        `: lệnh ghép không khớp quyền ⇒ bị từ chối. Muốn xem trạng thái thì gọi RIÊNG \`git -C ${worktree} status\`, rồi RIÊNG \`git -C ${worktree} diff\` (nếu được cấp).`,
        // Dogfood 3.34.2 (ME-2026-c6f2f5): Claude Code's denial text is
        // generic ("Permission to use Bash has been denied … don't ask
        // mode"); a worker read it as "Bash is off for the whole session",
        // never ran `npm test`, and reported blocked (verification_not_run).
        ' Thông báo "Permission to use Bash has been denied … don\'t ask mode" chỉ áp cho ĐÚNG lệnh vừa gọi — Bash vẫn chạy được mọi lệnh khớp quyền ở trên. Một lệnh bị từ chối KHÔNG phải lý do khai blocked — chạy lại đúng dạng lệnh đơn rồi làm tiếp; chỉ khai blocked khi việc cần một quyền thật sự không được cấp.',
        runsTests
          ? ` Trước khi khai blocked/failed với lý do verification_not_run (chưa chạy được test), PHẢI đã gọi đơn lẻ ít nhất một lần ${examples[0]} (lệnh test khớp quyền) và thấy chính lệnh đó bị từ chối/lỗi.`
          : "",
      ].join(""),
    );
  }
  lines.push(
    `- ticket_revision phải khớp ${ticket.revision}; changed_files[] chỉ trong allowed_paths ở trên.`,
  );
  lines.push(
    "- input_hash: KHÔNG đưa vào result — controller tự tính lúc dispatch; giá trị tự điền làm attempt bị từ chối.",
  );
  lines.push(
    "- uncovered_areas[] / remaining_work[]: KHÔNG bắt buộc (schema chấp nhận thiếu), nhưng để trống khi có phần liên quan CHƯA chạy/chưa kiểm là khai sai (điều cấm 6). uncovered_areas = cái liên quan chưa kiểm (vd môi trường/trình duyệt/dữ liệu thật chưa chạy); remaining_work = việc hoặc quyền cần người. Mỗi phần tử là 1 câu ngắn không rỗng; controller đưa vào mô tả MR ('Chưa kiểm chứng' / 'Còn lại'). KHÔNG dùng thay cho evidence[].",
  );
  lines.push(
    '- evidence[].sha256: schema yêu cầu hex64, nhưng KHÔNG có tool nào được cấp để tự tính sha256 thật (không có Bash(shasum*)/Bash(sha256sum*)). KHÔNG tự bịa số ngẫu nhiên trông giống hash — dùng ĐÚNG chuỗi 64 ký tự "0" lặp lại (`"0".repeat(64)`, tức "0000...0000") làm giá trị placeholder cho field này; controller KHÔNG đọc lại giá trị sha256 để verify (recipe_id phải đúng recipe của ticket; pass/fail do controller tự chạy recipe quyết định), nên placeholder không làm attempt bị từ chối.',
  );
  if (evidenceRequired) {
    // Mission ME-2026-f20511 (2026-09-15): 3/3 real implement attempts sent
    // `evidence: []` — code right, tests green — because "report thật đã
    // ghi" read as "a report I wrote", which no grant lets this adapter do,
    // and a stale RED report from the testcase ticket was on disk. The
    // verifier's actual contract: name the approved recipe + its
    // report_glob; the controller runs the recipe and writes the report.
    lines.push(
      `- evidence[]: BẮT BUỘC đúng 1 mục cho recipe "${ticket.verification_recipe}" — recipe_id = "${ticket.verification_recipe}", report_path = giá trị report_glob của recipe đó trong .avatar/verification-profile.json (đọc file để lấy), exit_code = kết quả lệnh test bạn tự chạy (0 khi GREEN). CONTROLLER sẽ tự chạy recipe sau khi nhận result và tự sinh report tại report_glob — bạn KHÔNG cần (và không được cấp quyền) chạy recipe hay ghi report; report cũ/RED còn trên đĩa từ attempt trước KHÔNG phải lý do để bỏ trống evidence. evidence: [] ⇒ attempt bị từ chối dù code đúng.`,
    );
  } else {
    // D-1 (close-out 2026-09-30): no claim about whether the ticket's own
    // verification_recipe is in the profile — true for review/bootstrap
    // tickets, false for `testaudit-v1` (its recipe is a real profile entry),
    // and this builder never reads the profile. Only what always holds.
    lines.push(
      `- evidence[]: adapter ${adapterId} KHÔNG chạy verification recipe — để mảng rỗng \`[]\`; KHÔNG bịa recipe_id. File staging đã ghi (nếu có) liệt kê trong summary.`,
    );
  }
  if (reviewReportRequired) {
    // Dogfood worker thật 2026-09-25: T-9002 (review-clean-v1) bị từ chối 3/3
    // lần `review_report vắng trong result` — verifier đòi field mà prompt
    // không hề nêu (playbook review-clean.md không được nạp, ví dụ không có
    // field). Nghĩa vụ này do CLI gác (result-verifier-checks.ts) nên phải
    // đứng NGAY TRONG contract, không phụ thuộc pack ghim có playbook hay không.
    lines.push(
      `- review_report: BẮT BUỘC cho adapter ${adapterId} — khai NGAY TRONG result JSON object {"risk": "low|medium|high|critical", "secret_scan": {"verdict": "clean|findings_present", "findings": <số phát hiện>}, "evidence_hashes": []} (schema strict, không field lạ; risk là mức bạn thật sự đo được trên diff). Thiếu field này ⇒ attempt bị từ chối (rejected), ticket KHÔNG done — kể cả khi đã ghi file report. Có thể ghi kèm bản sao .avatar/attempt-output/<run_id>/review-report.json để đối chiếu (controller dùng bản trong result; hai bản lệch thì bị ghi nhận), nhưng file KHÔNG thay được field trong result.`,
    );
  }
  lines.push(
    "- KHÔNG tự sửa ticket trực tiếp — mọi thay đổi TICKET (tạo/sửa/huỷ) đi qua proposed_changes để supervisor validate/áp dụng; với op create, content = ticket theo templates/autopilot/proposed-change-content.schema.json (object, KHÔNG phải markdown string) — tức ticket.schema.json NHƯNG KHÔNG có `id` (id lấy từ tên file trong target; gửi `id` ⇒ bị từ chối `Unrecognized key`).",
  );
  lines.push(
    "- BLUEPRINT.md/PLAN.md/file staging khác (vd .avatar/attempt-output/<run_id>/CURRENT_STATE.md) KHÔNG phải ticket — ghi trực tiếp bằng Write/Edit trong allowed_paths hoặc staging dir, KHÔNG khai qua proposed_changes (supervisor sẽ từ chối áp vì content không khớp ticket schema).",
  );
  lines.push("");
  lines.push(
    "**Định dạng câu trả lời cuối cùng (BẮT BUỘC):** câu trả lời PHẢI kết thúc bằng ĐÚNG MỘT khối ```json chứa result, KHÔNG viết gì thêm sau khối đó (không giải thích, không tóm tắt). Giải thích (nếu cần) đặt TRƯỚC khối JSON. Thiếu khối này = contract_error, kể cả khi việc đã làm xong trên đĩa.",
  );
  lines.push("");
  lines.push(
    "Ví dụ khung điền tối thiểu (điền run_id/session_id NGUYÊN VĂN từ binding, KHÔNG chép giá trị mẫu):",
  );
  lines.push("```json");
  lines.push(
    JSON.stringify(
      {
        schema_version: 1,
        mission_id: "<mission_id từ ticket>",
        ticket_id: ticket.id,
        ticket_revision: ticket.revision,
        run_id: "<run_id nguyên văn từ binding>",
        session_id: "<session_id nguyên văn từ binding>",
        attempt_no: 1,
        base_sha: "<sha git đầu attempt>",
        outcome: "done",
        error_class: null,
        summary: "<tóm tắt ngắn>",
        changed_files: [],
        evidence: evidenceRequired
          ? [
              {
                recipe_id: ticket.verification_recipe,
                report_path: "<report_glob của recipe này trong .avatar/verification-profile.json>",
                sha256: "0".repeat(64),
                exit_code: 0,
              },
            ]
          : [],
        proposed_tickets: [],
        proposed_changes: [],
        decisions: [],
        options: [],
        sources: [],
        ...(reviewReportRequired ? { review_report: REVIEW_REPORT_EXAMPLE } : {}),
      },
      null,
      2,
    ),
  );
  lines.push("```");
  return lines;
}
